import type { AuthState } from './auth/model.js';
import { renderSignIn } from './auth/view.js';
import type { MspHealthPrototypeModel } from './model.js';
import type { SettingsPageModel } from './settings/model.js';
import type { ClientSectionId, SettingsSectionId } from './settings/routing.js';
import { renderSettings, renderSettingsLoading } from './settings/view.js';
import {
  renderBackups,
  renderDashboard,
  renderPlannedModule,
  renderMicrosoft365Module,
} from './view.js';
import { canAccessNavigation } from './auth/permissions.js';
import { renderPlatformAdministration } from './platform-admin/view.js';
import type { PlatformAdministrationModel } from './platform-admin/model.js';
import type { ClientDirectory } from '../clients/model.js';
import type { ClientOnboardingConfiguration } from '../onboarding/model.js';
import type { OnboardingRequestDetail, OnboardingRequestListItem } from '../onboarding/request.js';
import { renderOnboardingOperations } from './onboarding/view.js';
import {
  renderClientDetail,
  renderClientDirectory,
  renderClientDirectoryLoading,
} from './clients/view.js';

export type ApplicationView =
  | 'OVERVIEW'
  | 'CLIENTS'
  | 'CLIENT_DETAIL'
  | 'BACKUPS'
  | 'SERVERS'
  | 'MICROSOFT_365'
  | 'THREECX'
  | 'ONBOARDING'
  | 'PROJECTS'
  | 'SETTINGS'
  | 'PLATFORM_ADMIN';

export function renderApplication(
  authState: AuthState,
  dashboard: MspHealthPrototypeModel,
  settings?: SettingsPageModel,
  requestedView: ApplicationView = 'OVERVIEW',
  requestedSettingsSection: SettingsSectionId | null = null,
  platformAdministration?: PlatformAdministrationModel,
  platformAdministrationFeedback = '',
  clientDirectory?: ClientDirectory,
  requestedClientId: string | null = null,
  clientFeedback = '',
  requestedClientSection: ClientSectionId | null = null,
  clientOnboarding?: ClientOnboardingConfiguration,
  rawOnboardingUrl: string | null = null,
  onboardingRequests?: readonly OnboardingRequestListItem[],
  onboardingRequestDetail: OnboardingRequestDetail | null = null,
  onboardingFeedback = '',
): string {
  if (authState.status !== 'AUTHENTICATED') return renderSignIn(authState);
  if (requestedView === 'SETTINGS' && canAccessNavigation(authState.user.role, 'SETTINGS')) {
    return settings === undefined
      ? renderSettingsLoading(authState.user, dashboard.managedClientCount)
      : renderSettings(
          settings,
          authState.user,
          dashboard.managedClientCount,
          requestedSettingsSection,
        );
  }
  if (
    requestedView === 'PLATFORM_ADMIN'
    && canAccessNavigation(authState.user.role, 'PLATFORM_ADMIN')
  ) {
    return renderPlatformAdministration(
      authState.user,
      dashboard.managedClientCount,
      platformAdministration,
      platformAdministrationFeedback,
    );
  }
  if (requestedView === 'CLIENT_DETAIL') {
    if (clientDirectory === undefined) {
      return renderClientDirectoryLoading(
        authState.user, dashboard.managedClientCount,
        clientFeedback || 'Loading client directory…',
      );
    }
    const client = clientDirectory?.clients.find((item) => item.id === requestedClientId) ?? null;
    return renderClientDetail(client, dashboard, authState.user, clientFeedback, requestedClientSection, clientOnboarding, rawOnboardingUrl);
  }
  if (requestedView === 'CLIENTS') return clientDirectory === undefined
    ? renderClientDirectoryLoading(
        authState.user, dashboard.managedClientCount,
        clientFeedback || 'Loading client directory…',
      )
    : renderClientDirectory(clientDirectory, dashboard, authState.user, clientFeedback);
  if (requestedView === 'BACKUPS') return renderBackups(dashboard, authState.user);
  if (requestedView === 'SERVERS') return renderPlannedModule(dashboard, authState.user, 'SERVERS', 'Servers');
  if (requestedView === 'MICROSOFT_365') {
    return renderMicrosoft365Module(dashboard, authState.user);
  }
  if (requestedView === 'THREECX') return renderPlannedModule(dashboard, authState.user, 'THREECX', '3CX');
  if (requestedView === 'ONBOARDING') {
    return renderOnboardingOperations(
      dashboard, authState.user, onboardingRequests, onboardingRequestDetail, onboardingFeedback,
    );
  }
  if (requestedView === 'PROJECTS') return renderPlannedModule(dashboard, authState.user, 'PROJECTS', 'Projects');
  return renderDashboard(dashboard, authState.user);
}
